// DS EDIT 5 — Multi-workspace RAG scope: pure helpers (no supabase imports).

export const RAG_SCOPE_MAX = 20;
export const RAG_PER_WORKSPACE_CAP = 5;

export interface ScopedRetrievalRow {
  documentId?: string;
  chunkText: string;
  similarity?: number;
}

/**
 * Normalize a workspace-id scope: trim, drop blanks, dedupe (order-preserving),
 * cap at RAG_SCOPE_MAX.
 */
export function normalizeRagScope(ids: ReadonlyArray<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of ids) {
    const id = (raw || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= RAG_SCOPE_MAX) break;
  }
  return out;
}

/** Client-side pre-validation mirror of the server rules (server is authoritative). */
export function validateRagScope(
  ids: ReadonlyArray<string>,
  membershipIds: ReadonlyArray<string>
): { ok: true } | { ok: false; error: string } {
  if (ids.length === 0) return { ok: false, error: "empty_scope" };
  if (ids.length > RAG_SCOPE_MAX) return { ok: false, error: "too_many_workspaces" };
  const memberSet = new Set(membershipIds);
  for (const id of ids) {
    if (!memberSet.has(id)) return { ok: false, error: `workspace_not_authorized:${id}` };
  }
  return { ok: true };
}

/** Parse the jsonb column value into a scope array; null/[]/non-array → null. */
export function parseStoredScope(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const ids = normalizeRagScope(value.filter((v): v is string => typeof v === "string"));
  return ids.length > 0 ? ids : null;
}

/**
 * Merge personal-lane and workspace-scoped retrieval rows: dedupe (a chunk
 * retrieved via both lanes counts once), sort by similarity desc.
 */
export function mergeScopedRetrievalRows<T extends ScopedRetrievalRow>(
  personal: ReadonlyArray<T>,
  scoped: ReadonlyArray<T>
): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const row of [...personal, ...scoped]) {
    const key = `${row.documentId ?? ""}::${row.chunkText.slice(0, 80)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out.sort((a, b) => (b.similarity ?? 0) - (a.similarity ?? 0));
}

/** Header badge label. */
export function scopeBadgeLabel(count: number): string {
  return count === 1 ? "Searching 1 workspace" : `Searching ${count} workspaces`;
}

/**
 * Workspace provenance for archived summaries: current workspace (or
 * "personal") plus the thread's RAG scope, deduped.
 */
export function buildProvenanceWorkspaceIds(
  currentWorkspaceId: string | null | undefined,
  scopeIds: ReadonlyArray<string> | null | undefined
): string[] {
  return normalizeRagScope([currentWorkspaceId || "personal", ...(scopeIds ?? [])]);
}
